import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Centrifuge } from "centrifuge";
import type { EventOf } from "@ichegou/contracts";
import { api, call } from "./api.ts";

export type LiveSnapshot = EventOf<"admin.live_snapshot">["data"] & { at: string };

/**
 * Live map state for a city: an HTTP snapshot first, then `admin.live_snapshot` pushes from
 * `admin:live`. While the socket is down it falls back to polling the snapshot.
 */
export function useLiveSnapshot(cityId: string) {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const key = ["live", cityId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => call(api.admin.live.snapshot.get({ query: { cityId } })) as Promise<LiveSnapshot>,
    refetchInterval: connected ? false : 10_000,
  });

  useEffect(() => {
    let cancelled = false;
    let client: Centrifuge | null = null;
    const tokens = () => call(api.admin.live.connect.post());
    void tokens().then((t) => {
      if (cancelled) return;
      client = new Centrifuge(t.url, { token: t.token, getToken: async () => (await tokens()).token });
      const sub = client.newSubscription(t.channel, { token: t.subscriptionToken, getToken: async () => (await tokens()).subscriptionToken });
      sub.on("publication", (ctx) => {
        const ev = ctx.data as { type: string; data: { cityId: string } };
        if (ev.type === "admin.live_snapshot" && ev.data.cityId === cityId) {
          qc.setQueryData(key, { ...(ev.data as LiveSnapshot), at: new Date().toISOString() });
        }
        if (ev.type === "driver.review_decided" || ev.type === "driver.liveness_decided" || ev.type === "driver.document_decided") void qc.invalidateQueries({ queryKey: ["drivers"] });
      });
      sub.on("subscribed", () => setConnected(true));
      sub.on("unsubscribed", () => setConnected(false));
      client.on("disconnected", () => setConnected(false));
      sub.subscribe();
      client.connect();
    }).catch(() => setConnected(false));
    return () => {
      cancelled = true;
      client?.disconnect();
      setConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityId]);

  return { ...q, connected };
}

/**
 * Discrete events on `admin:live` (not the snapshots), e.g. `trip.safety_alert` on Segurança.
 * Returns whether the socket is up, so the screen can poll while it is not.
 */
export function useAdminLiveEvent(type: string | string[], onEvent: (data: unknown, type: string) => void, enabled = true) {
  const types = Array.isArray(type) ? type.join(",") : type;
  const [connected, setConnected] = useState(false);
  const handler = useRef(onEvent);
  handler.current = onEvent;
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let client: Centrifuge | null = null;
    const tokens = () => call(api.admin.live.connect.post());
    void tokens().then((t) => {
      if (cancelled) return;
      client = new Centrifuge(t.url, { token: t.token, getToken: async () => (await tokens()).token });
      const sub = client.newSubscription(t.channel, { token: t.subscriptionToken, getToken: async () => (await tokens()).subscriptionToken });
      sub.on("publication", (ctx) => {
        const ev = ctx.data as { type: string; data: unknown };
        if (types.split(",").includes(ev.type)) handler.current(ev.data, ev.type);
      });
      sub.on("subscribed", () => setConnected(true));
      sub.on("unsubscribed", () => setConnected(false));
      client.on("disconnected", () => setConnected(false));
      sub.subscribe();
      client.connect();
    }).catch(() => setConnected(false));
    return () => {
      cancelled = true;
      client?.disconnect();
      setConnected(false);
    };
  }, [types, enabled]);
  return connected;
}
